import {Button} from 'primereact/button';
import {memo, useCallback, useLayoutEffect} from 'react';
import {useAppDispatch, useAppSelector, useTranslation} from '../hooks';

import {useLocation, useNavigate} from 'react-router-dom';
import {TurkeyFlag, UKFlag} from '../assets/icons';
import localization from '../locale/localization.json';
import {
  LanguageOptions,
  languageState,
  setTheme,
  switchLanguage,
  themeState,
  ThemeTypes,
} from '../redux/slices/app';
import {Localization} from '../types';
import {LANGS} from '../utils';

const AppSettings = () => {
  const dispatch = useAppDispatch();
  const selectedTheme = useAppSelector(themeState);
  const selectedLanguage = useAppSelector(languageState);
  const location = useLocation();
  const navigate = useNavigate();
  const {t} = useTranslation();

  const toggleTheme = useCallback(
    (theme: ThemeTypes) => {
      let themeLink = document.getElementById('app-theme');
      themeLink?.setAttribute('href', `/themes/md-${theme}-indigo/theme.css`);
      dispatch(setTheme(theme));
    },
    [dispatch],
  );

  useLayoutEffect(() => {
    toggleTheme(selectedTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTheme]);

  useLayoutEffect(() => {
    const currentLang = window.location.pathname.split('/')[1];
    if (LANGS.includes(currentLang) && currentLang !== selectedLanguage) {
      dispatch(switchLanguage(currentLang as LanguageOptions));
    }
  }, [location.pathname, dispatch, selectedLanguage]);

  const changeLanguage = useCallback(
    (targetLang: LanguageOptions) => {
      const selectedLanglocalizedData = (localization as Localization)[
        selectedLanguage
      ];
      const targetLanglocalizedData = (localization as Localization)[
        targetLang
      ];
      dispatch(switchLanguage(targetLang));

      const allPaths = Object.entries(selectedLanglocalizedData).filter(
        ([key]) => key.startsWith('path'),
      );
      const currentPath = location.pathname.replace(
        new RegExp(`^/${selectedLanguage}`),
        '',
      );
      const pathName = allPaths.find((p) => p[1] === currentPath);

      if (pathName?.[0]) {
        navigate(
          '/' + targetLang + targetLanglocalizedData[pathName?.[0] as string],
          {replace: false},
        );
      }
    },
    [dispatch, location, navigate, selectedLanguage],
  );

  return (
    <div className="flex gap-1 justify-content-end">
      <div className="hidden"></div>
      <Button
        className="no-padding-button"
        aria-label={t('changeLanguageTr')}
        onClick={() => changeLanguage('tr')}>
        <TurkeyFlag />
      </Button>
      <Button
        className="no-padding-button"
        aria-label={t('changeLanguageEn')}
        onClick={() => changeLanguage('en')}>
        <UKFlag />
      </Button>
      <Button
        className="theme-button"
        aria-label={
          selectedTheme === 'dark'
            ? t('toggleThemeLightMode')
            : t('toggleThemeDarkMode')
        }
        icon={`pi pi-${selectedTheme === 'dark' ? 'sun' : 'moon'}`}
        style={{
          color:
            selectedTheme === 'dark' ? 'var(--orange-400)' : 'var(--blue-500)',
        }}
        onClick={() => toggleTheme(selectedTheme === 'dark' ? 'light' : 'dark')}
      />
    </div>
  );
};

export default memo(AppSettings);
